import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useCondos } from "context/CondosContext";
import { useCases } from "context/CasesContext";
import { CasePanel } from "components/CasePanel";
import { Asset, CaseStatus, IssueType } from "types/CondoCase";
import { Select } from "components/ui/Select";
import Spinner from "components/ui/Spinner";
import { AlertCircle, ChevronLeft } from "lucide-react";
import { formatTimestamp } from "utils/dateFormat";
import { useAuth } from "context/AuthContext";
import { MOBILE_PAGE_TITLE_MENU_INSET } from "constants/pageLayout";
import fetchWithCredentials from "apiClient";
import { resolveAccessibleCondoId } from "utils/accessibleCondo";

const TYPE_LABELS_ES: Record<IssueType, string> = {
  issue_high: "Alta prioridad",
  issue_medium: "Prioridad media",
  issue_low: "Baja prioridad",
};

const TYPE_LABELS_EN: Record<IssueType, string> = {
  issue_high: "High priority",
  issue_medium: "Medium priority",
  issue_low: "Low priority",
};

const TYPE_COLORS: Record<IssueType, string> = {
  issue_high: "text-red-600",
  issue_medium: "text-orange-600",
  issue_low: "text-yellow-600",
};

const STATUS_LABELS_ES: Record<CaseStatus, string> = {
  open: "Abierto",
  closed: "Cerrado",
};

const STATUS_LABELS_EN: Record<CaseStatus, string> = {
  open: "Open",
  closed: "Closed",
};

export default function Cases() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { condos, loading: condosLoading } = useCondos();
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const TYPE_LABELS = locale === "es" ? TYPE_LABELS_ES : TYPE_LABELS_EN;
  const STATUS_LABELS = locale === "es" ? STATUS_LABELS_ES : STATUS_LABELS_EN;
  const {
    cases,
    loading: casesLoading,
    error: casesError,
    refresh,
  } = useCases();

  // Use local state for condo selection in this screen (independent from Home screen)
  const [selectedCondoId, setSelectedCondoId] = useState<number | null>(null);
  const [urlHydrated, setUrlHydrated] = useState(false);

  const [statusFilter, setStatusFilter] = useState<CaseStatus>(
    (searchParams.get("status") as CaseStatus) || "open",
  );
  const [assetFilter, setAssetFilter] = useState<number | null>(() => {
    const assetFromUrl = searchParams.get("asset");
    return assetFromUrl ? parseInt(assetFromUrl) : null;
  });
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [titleSearch, setTitleSearch] = useState<string>("");
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(
    searchParams.get("case") ? parseInt(searchParams.get("case")!) : null,
  );
  const [mobileCaseListOpen, setMobileCaseListOpen] = useState(
    () => !searchParams.get("case"),
  );

  // Apply condo from the URL after the portfolio is loaded. Ignore ids outside
  // this manager's buildings. Gate URL sync until then so the landing query
  // is not wiped while condos are still loading.
  useEffect(() => {
    if (condosLoading) {
      return;
    }
    const condoIdFromUrl = searchParams.get("condo");
    if (condoIdFromUrl) {
      setSelectedCondoId(resolveAccessibleCondoId(condoIdFromUrl, condos));
    }
    setUrlHydrated(true);
  }, [condos, condosLoading, searchParams]);

  useEffect(() => {
    if (!urlHydrated || condosLoading || selectedCondoId == null) {
      return;
    }
    if (!condos.some((condo) => condo.id === selectedCondoId)) {
      setSelectedCondoId(null);
    }
  }, [urlHydrated, condos, condosLoading, selectedCondoId]);

  useEffect(() => {
    let cancelled = false;
    setAssetsLoading(true);

    const assetsUrl = selectedCondoId
      ? `/manager-api/assets/?condo=${selectedCondoId}`
      : "/manager-api/assets/";

    fetchWithCredentials(assetsUrl)
      .then((data: Asset[]) => {
        if (!cancelled) {
          setAssets(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAssets([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAssetsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      setAssetsLoading(false);
    };
  }, [selectedCondoId]);

  useEffect(() => {
    if (
      assetFilter &&
      !assets.some((asset) => asset.id === assetFilter)
    ) {
      setAssetFilter(null);
    }
  }, [assetFilter, assets]);

  // Fetch cases when condo, status, or asset filter changes
  useEffect(() => {
    refresh(selectedCondoId, statusFilter, assetFilter);
  }, [selectedCondoId, statusFilter, assetFilter, refresh]);

  // Update URL when filters or selection change (after deep-link hydration).
  useEffect(() => {
    if (!urlHydrated) {
      return;
    }
    const params = new URLSearchParams();
    if (selectedCondoId) {
      params.set("condo", selectedCondoId.toString());
    }
    if (statusFilter) {
      params.set("status", statusFilter);
    }
    if (assetFilter) {
      params.set("asset", assetFilter.toString());
    }
    if (selectedCaseId) {
      params.set("case", selectedCaseId.toString());
    }
    setSearchParams(params, { replace: true });
  }, [
    urlHydrated,
    selectedCondoId,
    statusFilter,
    assetFilter,
    selectedCaseId,
    setSearchParams,
  ]);

  // Filter cases by title (frontend filtering)
  // Note: cases from backend are already sorted by severity (type_priority) then by created_at descending
  const filteredCases = useMemo(() => {
    let filtered = [...cases];
    if (titleSearch.trim()) {
      const searchLower = titleSearch.toLowerCase().trim();
      filtered = filtered.filter((c) =>
        c.title.toLowerCase().includes(searchLower),
      );
    }
    // Maintain the original order from backend (sorted by severity then date)
    return filtered;
  }, [cases, titleSearch]);

  // Find selected case
  const selectedCase = selectedCaseId
    ? filteredCases.find((c) => c.id === selectedCaseId) || null
    : null;

  // Selection no longer appears in the list (building/status/title filters, refresh, etc.)
  useEffect(() => {
    if (casesLoading || selectedCaseId == null) return;
    if (selectedCase == null) {
      setSelectedCaseId(null);
      setMobileCaseListOpen(true);
    }
  }, [casesLoading, selectedCaseId, selectedCase]);

  useEffect(() => {
    if (!selectedCaseId) {
      setMobileCaseListOpen(true);
    }
  }, [selectedCaseId]);

  return (
    <div className="w-full h-full flex flex-col bg-gray-50">
      <div className="border-b border-gray-200 bg-white">
        <div
          className={`border-b border-gray-200 py-4 lg:px-6 ${MOBILE_PAGE_TITLE_MENU_INSET}`}
        >
          <h1 className="text-2xl font-bold text-gray-900">
            {locale === "es" ? "Incidentes" : "Incidents"}
          </h1>
        </div>

        <div className="border-b border-gray-200 px-4 py-4 lg:px-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2.5">
              {locale === "es" ? "Edificio" : "Building"}
            </label>
            <Select
              value={selectedCondoId?.toString() || ""}
              onChange={(value) => {
                const newCondoId = value ? parseInt(value) : null;
                setSelectedCondoId(newCondoId);
                setSelectedCaseId(null);
                setMobileCaseListOpen(true);
                // Update URL immediately
                const params = new URLSearchParams(searchParams);
                if (newCondoId) {
                  params.set("condo", newCondoId.toString());
                } else {
                  params.delete("condo");
                }
                params.delete("asset");
                params.delete("case");
                setSearchParams(params, { replace: true });
              }}
              options={[
                {
                  value: "",
                  label:
                    locale === "es"
                      ? "Todos los edificios"
                      : "All buildings",
                },
                ...condos.map((condo) => ({
                  value: condo.id.toString(),
                  label: condo.name,
                })),
              ]}
              disabled={condosLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2.5">
              {locale === "es" ? "Estado" : "Status"}
            </label>
            <Select
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value as CaseStatus);
                setSelectedCaseId(null);
                setMobileCaseListOpen(true);
              }}
              options={[
                { value: "open", label: STATUS_LABELS.open },
                { value: "closed", label: STATUS_LABELS.closed },
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2.5">
              {locale === "es" ? "Activo" : "Asset"}
            </label>
            <Select
              value={assetFilter?.toString() || ""}
              onChange={(value) => {
                setAssetFilter(value ? parseInt(value) : null);
                setSelectedCaseId(null);
                setMobileCaseListOpen(true);
              }}
              options={[
                {
                  value: "",
                  label:
                    locale === "es" ? "Todos los activos" : "All assets",
                },
                ...assets.map((asset) => ({
                  value: asset.id.toString(),
                  label:
                    selectedCondoId || !asset.condoName
                      ? asset.name
                      : `${asset.name} (${asset.condoName})`,
                })),
              ]}
              disabled={assetsLoading}
            />
          </div>
        </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        {selectedCase && mobileCaseListOpen && (
          <button
            type="button"
            className="absolute inset-0 z-[15] bg-black/40 lg:hidden"
            aria-label={locale === "es" ? "Cerrar lista" : "Close list"}
            onClick={() => setMobileCaseListOpen(false)}
          />
        )}

        {/* Cases List */}
        <div
          className={`absolute inset-y-0 left-0 z-20 flex w-[min(100%,22rem)] max-w-full flex-col overflow-hidden border-r border-gray-200 bg-white shadow-xl transition-transform duration-300 ease-in-out lg:static lg:z-auto lg:w-96 lg:max-w-none lg:translate-x-0 lg:shadow-none ${
            mobileCaseListOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {/* Search Box */}
          <div
            className="border-b border-gray-200 px-3 pb-3 pt-12 lg:p-3 lg:pt-3"
          >
            <input
              type="text"
              value={titleSearch}
              onChange={(e) => setTitleSearch(e.target.value)}
              placeholder={
                locale === "es" ? "Buscar por título..." : "Search by title..."
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Cases List Content */}
          <div className="flex-1 overflow-y-auto">
            {casesLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : casesError ? (
              <div className="p-4 text-red-500 text-sm">{casesError}</div>
            ) : filteredCases.length === 0 ? (
              <div className="p-4 text-gray-500 text-sm">
                {locale === "es"
                  ? "No se encontraron incidentes para los filtros seleccionados"
                  : "No incidents found for the selected filters"}
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredCases.map((caseItem) => (
                  <div
                    key={caseItem.id}
                    onClick={() => {
                      setSelectedCaseId(caseItem.id);
                      setMobileCaseListOpen(false);
                    }}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedCaseId === caseItem.id
                        ? "bg-blue-100 border border-blue-300"
                        : "hover:bg-gray-50 border border-transparent"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">
                          {caseItem.title}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate">
                          {caseItem.condoName}
                          {caseItem.assetName
                            ? ` • ${caseItem.assetName}`
                            : ""}
                          {caseItem.reportedBy
                            ? ` • ${
                                locale === "es" ? "Reportado por" : "Reported by"
                              } ${caseItem.reportedBy.name}`
                            : ""}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div
                            className={`text-xs ${
                              TYPE_COLORS[caseItem.type] || "text-gray-500"
                            }`}
                          >
                            {TYPE_LABELS[caseItem.type]}
                          </div>
                          {caseItem.createdAt && (
                            <span className="text-xs text-gray-400">
                              • {formatTimestamp(caseItem.createdAt, locale)}
                            </span>
                          )}
                        </div>
                        {caseItem.summary && (
                          <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {caseItem.summary}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            caseItem.status === "open"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {STATUS_LABELS[caseItem.status]}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Case Details */}
        <div
          className={
            mobileCaseListOpen
              ? "hidden min-h-0 min-w-0 flex-1 overflow-hidden lg:flex lg:flex-col"
              : "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
          }
        >
          {selectedCase ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div
                className="flex shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-3 py-2 max-lg:pr-3 lg:hidden"
              >
                <button
                  type="button"
                  onClick={() => setMobileCaseListOpen(true)}
                  className="flex min-h-10 min-w-0 flex-1 touch-manipulation items-center gap-2 py-1 text-left text-gray-900 active:bg-gray-200/50"
                  aria-label={
                    locale === "es" ? "Ver lista de incidentes" : "Show incident list"
                  }
                >
                  <span className="flex shrink-0 items-center justify-center rounded-full p-1.5 text-gray-700 hover:bg-gray-200/80">
                    <ChevronLeft className="h-6 w-6" strokeWidth={2} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">
                    {selectedCase.title}
                  </span>
                </button>
              </div>
              <div
                className="flex min-h-0 flex-1 flex-col overflow-hidden max-lg:px-3 max-lg:pb-4 max-lg:pt-2 lg:p-4"
              >
                <CasePanel
                  case={selectedCase}
                  onUpdate={async (updatedCase) => {
                    // Refresh the cases list to get the updated data
                    await refresh(selectedCondoId, statusFilter, assetFilter);
                    // The selectedCase will automatically update from the refreshed cases
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">
                  {locale === "es"
                    ? "Selecciona un incidente para ver los detalles"
                    : "Select an incident to view details"}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
